import React from 'react';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';
import { PlatformProvider } from './contexts/PlatformContext';
import { AuthProvider } from './contexts/AuthContext';
import { SiteHeader } from './components/public/SiteHeader';
import { SiteFooter } from './components/public/SiteFooter';
import { Home } from './pages/public/Home';
import { Register } from './pages/public/Register';
import { About } from './pages/public/About';
import { Contact } from './pages/public/Contact';
import { ComingNext } from './pages/public/ComingNext';
import { VelnorisAssistant } from './components/assistant/VelnorisAssistant';
import { RegistryProvider } from './contexts/RegistryContext';
import { AdminConsole } from './pages/admin/AdminConsole';
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  React.useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, [pathname]);
  return null;
}

/** Routes whose data models and content are seeded; screens land in a later phase. */
const pendingRoutes: {
  path: string;
  title: string;
  phase: string;
  detail: string;
}[] = [{
  path: '/services',
  title: 'Executive Search',
  phase: 'Phase 1',
  detail: 'Twelve engagement types are fully specified — audience, organizational challenge, methodology, deliverables and confidentiality safeguards for each. The service index and detail pages are next in the build.'
}, {
  path: '/services/:slug',
  title: 'Service detail',
  phase: 'Phase 1',
  detail: 'This service is specified in full and its page is scheduled next in the build.'
}, {
  path: '/board-c-suite-search',
  title: 'Board and C-Suite Search',
  phase: 'Phase 1',
  detail: 'The board practice and C-suite practice pages draw on the same specified service records and are scheduled next.'
}, {
  path: '/industries',
  title: 'Industries',
  phase: 'Phase 1',
  detail: 'Fourteen industry practices are authored with overviews, market considerations, leadership functions and sample role categories. The searchable index and detail pages are next in the build.'
}, {
  path: '/industries/:slug',
  title: 'Industry detail',
  phase: 'Phase 1',
  detail: 'This practice is authored in full and its page is scheduled next in the build.'
}, {
  path: '/opportunities',
  title: 'Leadership Opportunities',
  phase: 'Phase 3',
  detail: 'Ten mandates are seeded across all four verification statuses, with the filter taxonomy, screening questions and internal verification records in place. The filtered directory and detail template follow the public site build.'
}, {
  path: '/opportunities/:slug',
  title: 'Opportunity detail',
  phase: 'Phase 3',
  detail: 'This mandate is recorded in full, including its verification evidence and confidentiality instruction. The detail template with its 27 fields is scheduled in Phase 3.'
}, {
  path: '/career-services',
  title: 'Executive Career Services',
  phase: 'Phase 1',
  detail: 'Nine optional, candidate-paid services are documented with scope, delivery terms, revision and refund policy, alongside the disclosure that purchase never influences recruitment consideration.'
}, {
  path: '/for-candidates',
  title: 'For Candidates',
  phase: 'Phase 2',
  detail: 'Registration, granular consent, presentation authorization and application tracking are all modelled. The candidate-facing explainer page and portal follow.'
}, {
  path: '/who-we-serve',
  title: 'Who We Serve',
  phase: 'Phase 1',
  detail: 'Employers, job seekers and industry practices each have their own route into the firm. Use the Who We Serve menu above to go straight to hiring solutions, job search, or a specific industry practice.'
}, {
  path: '/employers/hiring-solutions',
  title: 'Hiring Solutions',
  phase: 'Phase 4',
  detail: 'Our engagement models — retained search, exclusive search, interim appointments and advisory — are specified in full and reachable from the Executive Search menu while this overview page is built.'
}, {
  path: '/employers/workforce-planning',
  title: 'Workforce Planning',
  phase: 'Phase 4',
  detail: 'Succession planning, market mapping and talent intelligence underpin our workforce planning work. Each is documented today under Executive Search.'
}, {
  path: '/employers/employer-branding',
  title: 'Employer Branding',
  phase: 'Phase 4',
  detail: 'How your organization is described to the market shapes who considers it. This advisory page is scheduled alongside the employer portal.'
}, {
  path: '/candidates/upload-resume',
  title: 'Upload Resume',
  phase: 'Phase 2',
  detail: 'Document upload with version history and consultant review is modelled and ready. It becomes available inside your candidate portal once registration screens land.'
}, {
  path: '/candidates/job-alerts',
  title: 'Job Alerts',
  phase: 'Phase 2',
  detail: 'Alert preferences are recorded at registration and adjustable in your portal. Confidential mandates are shared to your portal rather than broadcast by email.'
}, {
  path: '/for-employers',
  title: 'For Employers',
  phase: 'Phase 4',
  detail: 'The eighteen-field search inquiry, verification gating and authorization-derived candidate visibility are modelled. The employer explainer page and portal follow.'
}, {
  path: '/insights',
  title: 'Insights',
  phase: 'Phase 1',
  detail: 'Eight articles are written across ten categories, with authors, publication and update dates, reading times and Article schema prepared. The index and article template are next.'
}, {
  path: '/insights/:slug',
  title: 'Article',
  phase: 'Phase 1',
  detail: 'This article is written in full and its page is scheduled next in the build.'
}, {
  path: '/legal/:slug',
  title: 'Legal',
  phase: 'Phase 6',
  detail: 'Seven legal documents are drafted as substantive placeholders: privacy policy, terms of use, candidate consent, cookie policy, accessibility statement, refund policy and the recruitment-services disclaimer. Each is flagged as requiring qualified legal review before publication.'
}, {
  path: '/sign-in',
  title: 'Sign In',
  phase: 'Phase 2',
  detail: 'Role-based sessions, the permission matrix and seeded test accounts for all seven role variants are in place. The sign-in screen follows in Phase 2.'
}, {
  path: '/portal/*',
  title: 'Portal',
  phase: 'Phases 2 to 5',
  detail: 'The candidate portal, employer portal, recruiter dashboard and administrator dashboard run on a seeded store that is already complete — applications, documents with version history, consent records, presentations, interviews, messaging, notifications and audit logging.'
}];
function PublicSite() {
  return <div className="flex min-h-screen w-full flex-col bg-white" data-id="element-0" data-mp="3i4oal2p5xzsq">
      <SiteHeader data-id="element-1" data-mp="3n2g05q2c59tr" />
      <div className="flex-1" data-id="element-2" data-mp="3n2zi36rzjy7s">
        <Routes data-id="element-3" data-mp="3n27n9y67q4ik">
          <Route path="/" element={<Home data-id="element-5" data-mp="385gtq5j7zn0v" />} data-id="element-4" data-mp="3n0n22z5sfxxd" />
          <Route path="/register" element={<Register data-id="element-7" data-mp="384xc6pxhf0nd" />} data-id="element-6" data-mp="3868hiu6ksggl" />
          <Route path="/about" element={<About data-id="element-9" data-mp="3d40j6fx0vgxz" />} data-id="element-8" data-mp="387tawgh5ltcv" />
          <Route path="/contact" element={<Contact data-id="element-11" data-mp="3d2p77stfb2vs" />} data-id="element-10" data-mp="3d3ih32noi8i5" />
          {pendingRoutes.map(r => <Route key={r.path} path={r.path} element={<ComingNext title={r.title} phase={r.phase} detail={r.detail} data-id="element-13" data-mp="344ddhhx9foz4" />} data-id="element-12" data-mp="2zc3tsjmqnl5a" />)}
          <Route path="*" element={<ComingNext title="Page not found" phase="404" detail="The page you were looking for does not exist. Use the navigation above, or return to the home page." data-id="element-15" data-mp="2pd4n36lj9zkx" />} data-id="element-14" data-mp="2pfqsazkyc2zz" />
        </Routes>
      </div>
      <SiteFooter data-id="element-16" data-mp="2khyyu14ns8to" />
      <VelnorisAssistant data-id="element-17" data-mp="30vdii53ser2h" />
    </div>;
}
export function App() {
  return <PlatformProvider data-id="element-18" data-mp="30tautrq3xa0x">
      <AuthProvider data-id="element-19" data-mp="2vwan7cdlzz7p">
        <RegistryProvider data-id="element-20" data-mp="2vw0xlpwh7u2j">
          <BrowserRouter data-id="element-21" data-mp="2vwtwy5t3vhan">
            <ScrollToTop data-id="element-22" data-mp="2vu7tah9c5lxp" />
            <Routes data-id="element-23" data-mp="2qwxsy6kerpv3">
              <Route path="/admin" element={<AdminConsole data-id="element-25" data-mp="2qvcwucig3yv4" />} data-id="element-24" data-mp="2qw67hokiz8ij" />
              <Route path="*" element={<PublicSite data-id="element-27" data-mp="2lyw9hkj2loj3" />} data-id="element-26" data-mp="2ly4o12j6t76j" />
            </Routes>
          </BrowserRouter>
        </RegistryProvider>
      </AuthProvider>
    </PlatformProvider>;
}